import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { BarcodeLookupResult } from './products.service';

export type ScanMatchStatus =
  | 'matched'
  | 'ambiguous'
  | 'partial_match'
  | 'no_match'
  | 'unknown_barcode'
  /** Same ingredients as a line that needs the exact prescribed product (MPS or do-not-substitute). */
  | 'different_product';

export type ScanMatchMethod = 'ingredient' | 'product_text' | 'pip';

export interface ScanMatchMedication {
  medicationId: string;
  medicationName: string;
  dosage: string;
  instruction?: string;
  quantity?: number;
}

export interface ScanMatchResult {
  status: ScanMatchStatus;
  matchMethod?: ScanMatchMethod;
  matchedMedication?: ScanMatchMedication;
  candidates?: ScanMatchMedication[];
  lookup?: BarcodeLookupResult | null;
}

@Injectable({
  providedIn: 'root',
})
export class BatchService {
  batchData: any = {};
  url: string = `${environment.apiUrl}v1/Batch`;

  constructor(private http: HttpClient) {
    // The old /batchpicklist page cached whole batches, patient names included; nothing reads
    // or clears that cache any more, so remove what is left of it.
    try { localStorage.removeItem('safescan.batch.cache'); } catch { /* private mode */ }
  }

  getAllBatch(
    pageNumber?: number,
    pageSize?: number,
    status?: string,
    userDetails?: string,
    storeId?: number | null,
  ) {
    const params: any = {};
    if (pageNumber !== undefined) params.PageNumber = pageNumber.toString();
    if (pageSize !== undefined) params.PageSize = pageSize.toString();
    if (status) params.Status = status;
    if (userDetails) params.UserDetails = userDetails;

    if (storeId !== null && storeId !== undefined) {
      params.StoreId = storeId.toString();
    }
    return this.http.get(`${this.url}/List`, { params });
  }

  getBatchById(batchId: string) {
    return this.http.get(`${this.url}/${batchId}`);
  }

  updateBatchStatus(batchId: string, status: string) {
    return this.http.put(`${this.url}/${batchId}/${status}`, {});
  }

  updateBatch(batch: any) {
    return this.http.put(`${this.url}`, { batch });
  }

  updateAvailability(updatePayload: any) {
    return this.http.put<any>(`${this.url}`, updatePayload);
  }

  matchScan(batchId: string, rxId: string, barcode: string): Observable<ScanMatchResult | null> {
    return this.http
      .post<{ result?: ScanMatchResult | null }>(`${this.url}/${batchId}/match-scan`, {
        barcode,
        rxId,
      })
      .pipe(
        map((res) => res?.result ?? null),
        catchError(() => of(null)),
      );
  }
}
